const SITE_URL = "https://privateshopper.co.uk";

export const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name: "My Private Shopper",
  alternateName: "MPS",
  url: SITE_URL,
  description:
    "My Private Shopper is a premium UK-to-Nigeria personal shopping concierge. We source, purchase, and ship UK products directly to any of Nigeria's 36 states on behalf of our clients.",
  areaServed: ["Nigeria", "United Kingdom"],
  serviceType: "Personal Shopping Concierge",
  priceRange: "££",
  currenciesAccepted: "GBP",
  email: "ops@privateshopper.co.uk",
};

export const servicePriceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "UK to Nigeria Personal Shopping",
  provider: {
    "@type": "Organization",
    name: "My Private Shopper",
    url: SITE_URL,
  },
  areaServed: ["Nigeria"],
  description:
    "Premium UK-to-Nigeria personal shopping concierge. We source, purchase, and ship UK products to any Nigerian state.",
  offers: [
    {
      "@type": "Offer",
      name: "Service Fee — £0–£100 product value",
      priceCurrency: "GBP",
      price: "20.00",
    },
    {
      "@type": "Offer",
      name: "Service Fee — £101–£300 product value",
      priceCurrency: "GBP",
      priceSpecification: { "@type": "UnitPriceSpecification", price: "15", priceCurrency: "GBP" },
      description: "15% of product value",
    },
    {
      "@type": "Offer",
      name: "Service Fee — £301–£600 product value",
      priceCurrency: "GBP",
      description: "12% of product value",
    },
    {
      "@type": "Offer",
      name: "Service Fee — £600+ product value",
      priceCurrency: "GBP",
      price: "75.00",
      description: "Minimum £75 (negotiated)",
    },
  ],
};

export const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.question,
    acceptedAnswer: { "@type": "Answer", text: f.answer },
  })),
});

export const articleSchema = ({ title, slug, excerpt, publishedAt, cover }) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: title,
  description: excerpt,
  image: cover ? [cover] : undefined,
  datePublished: publishedAt,
  dateModified: publishedAt,
  author: { "@type": "Organization", name: "My Private Shopper" },
  publisher: {
    "@type": "Organization",
    name: "My Private Shopper",
    url: SITE_URL,
  },
  mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}/blog/${slug}` },
});

export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: item.name,
    item: item.url ? `${SITE_URL}${item.url}` : undefined,
  })),
});

export const speakableSchema = (cssSelectors) => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  speakable: {
    "@type": "SpeakableSpecification",
    cssSelector: cssSelectors,
  },
});

/** Inject a JSON-LD block on a page. */
export function JsonLd({ data }) {
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
}

/** Set <title>, meta description, OG, canonical via DOM (no helmet dependency). */
export function setPageMeta({ title, description, canonical = "", image }) {
  if (typeof document === "undefined") return;
  document.title = title;
  const ensureMeta = (key, value, isProperty = false) => {
    if (value == null) return;
    const attr = isProperty ? "property" : "name";
    let el = document.head.querySelector(`meta[${attr}="${key}"]`);
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute(attr, key);
      document.head.appendChild(el);
    }
    el.setAttribute("content", value);
  };
  ensureMeta("description", description);
  ensureMeta("og:title", title, true);
  ensureMeta("og:description", description, true);
  ensureMeta("og:type", "website", true);
  if (canonical) ensureMeta("og:url", canonical, true);
  if (image) ensureMeta("og:image", image, true);
  ensureMeta("twitter:card", "summary_large_image");
  ensureMeta("twitter:title", title);
  ensureMeta("twitter:description", description);
  if (image) ensureMeta("twitter:image", image);
  if (canonical) {
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", canonical);
  }
}
